import { useMemo, useCallback } from 'react'
import { useLazyQuery } from '@apollo/client/react'
import { SEARCH_BUSINESS_PARTNERS_AUTOCOMPLETE } from 'graphql/queries/businessPartners/businessPartnerQueries'
import { debounce } from 'lodash'

const DEBOUNCE_MS = 300

type BusinessPartnerOption = { value: string; label: string; id: string }

const useBusinessPartnersAutocompleteSetup = () => {
  const [fetchBusinessPartners] = useLazyQuery(
    SEARCH_BUSINESS_PARTNERS_AUTOCOMPLETE,
    { fetchPolicy: 'no-cache' },
  )

  const debouncedFetch = useMemo(
    () =>
      debounce(
        async (
          query: string,
          resolve: (options: BusinessPartnerOption[]) => void,
        ) => {
          try {
            const { data } = await fetchBusinessPartners({
              variables: { query },
            })
            resolve(
              (data?.searchBusinessPartners || []).map((item) => ({
                value: item.id,
                label: item.name,
                id: item.id,
              })),
            )
          } catch (error) {
            console.error('Error fetching business partners:', error)
            resolve([])
          }
        },
        DEBOUNCE_MS,
      ),
    [fetchBusinessPartners],
  )

  const handleBusinessPartnerSearchChange = useCallback(
    (searchText: string) =>
      new Promise<BusinessPartnerOption[]>((resolve) => {
        const sanitized = searchText?.trim() || ''
        const query = sanitized.length < 3 ? '*' : sanitized
        debouncedFetch(query, resolve)
      }),
    [debouncedFetch],
  )

  return { handleBusinessPartnerSearchChange }
}

export default useBusinessPartnersAutocompleteSetup
